/**
 * Token Input Modal - For localhost development token entry
 */

import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './TokenInputModal.css';

interface TokenInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function TokenInputModal({ isOpen, onClose, onSuccess }: TokenInputModalProps) {
  const [token, setToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { verifyMagicLinkToken } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token.trim()) {
      return;
    }

    setIsLoading(true);

    const success = await verifyMagicLinkToken(token.trim());

    if (success) {
      setToken('');
      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    }

    setIsLoading(false);
  };

  const handleClose = () => {
    setToken('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: clicking overlay closes modal
    <div
      className="token-input-overlay"
      onClick={handleClose}
      onKeyDown={(e) => e.key === 'Escape' && handleClose()}
    >
      <div
        className="token-input-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="token-input-title"
        aria-modal="true"
      >
        <div className="token-input-header">
          <h2 id="token-input-title">🔧 Enter Sign-In Token</h2>
          <button
            type="button"
            className="token-input-close"
            onClick={handleClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="token-input-body">
          <p className="token-input-description">
            For localhost development, enter the token from your email or click the magic link in
            the email.
          </p>

          <form onSubmit={handleSubmit} className="token-input-form">
            <div className="form-group">
              <label htmlFor="token">Sign-in token</label>
              <input
                id="token"
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Paste your token here"
                required
                disabled={isLoading}
                autoComplete="off"
                spellCheck="false"
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={isLoading || !token.trim()}>
              {isLoading ? 'Verifying...' : 'Sign In'}
            </button>
          </form>

          <p className="token-input-help">
            💡 <strong>Tip:</strong> The token is a 32-character string in your email. You can also
            just click the magic link in the email instead.
          </p>
        </div>
      </div>
    </div>
  );
}
